// Calculs purs de la fenêtre « Terrain du site » (SiteParcelsModal.js) : lecture d'une liste
// d'identifiants collée, voisinage des parcelles, sélection au lasso, totaux et contrôles.
// Les géométries sont du GeoJSON en WGS84 ([lon, lat]), comme le WFS cadastre de l'IGN et
// l'API (/cbs_parcel). Les distances passent par une projection locale en mètres, suffisante
// à l'échelle d'un site.

const METERS_PER_DEGREE = 111320;
export const ADJACENCY_TOLERANCE_M = 1;
export const LASSO_MIN_SHARE = 0.5;
export const PLOT_SURFACE_TOLERANCE = 0.1;
const IDU = /^\d{2}[\dAB]\d{2}\d{3}[0-9A-Z]{2}\d{4}$/;

export const isIdu = (value) => IDU.test(value);

// « 64102000AK0730 », « AK 730 », « AK0730 », « 0A 853 », « A853 »… séparés par des virgules,
// points-virgules, retours à la ligne ou « et ». Les références sans commune prennent celle de
// `defaultCommune` (8 premiers caractères d'un identifiant : INSEE + préfixe, ex. 64102000).
export function parseParcelList(text, defaultCommune) {
  const ids = [];
  const unparsed = [];
  const tokens = (text || '').toUpperCase().split(/[,;\n\r\t]+|\bET\b/).map((t) => t.trim()).filter(Boolean);
  tokens.forEach((token) => {
    const compact = token.replace(/[\s.\-/]+/g, '');
    if (isIdu(compact)) {
      ids.push(compact);
      return;
    }
    const match = compact.match(/^(\d{2}[\dAB]\d{2}\d{3})?([0-9]?[A-Z]{1,2})(\d{1,4})$/);
    const commune = match && (match[1] || defaultCommune);
    if (!match || !commune) {
      unparsed.push(token);
      return;
    }
    ids.push(`${commune}${match[2].padStart(2, '0')}${match[3].padStart(4, '0')}`);
  });
  return { ids: [...new Set(ids)], unparsed };
}

export const outerRings = (geometry) => {
  if (!geometry) return [];
  const polygons = geometry.type === 'MultiPolygon' ? geometry.coordinates : [geometry.coordinates];
  return polygons.map((polygon) => polygon[0]);
};

// [lat, lng] pour react-leaflet
export const toLatLngs = (geometry) => outerRings(geometry).map((ring) => ring.map(([lon, lat]) => [lat, lon]));

const bounds = (rings) => {
  const points = rings.flat();
  return {
    minLon: Math.min(...points.map((p) => p[0])), maxLon: Math.max(...points.map((p) => p[0])),
    minLat: Math.min(...points.map((p) => p[1])), maxLat: Math.max(...points.map((p) => p[1])),
  };
};

const projector = (latitude) => {
  const kx = METERS_PER_DEGREE * Math.cos((latitude * Math.PI) / 180);
  return ([lon, lat]) => [lon * kx, lat * METERS_PER_DEGREE];
};

const pointSegmentDistance = ([px, py], [ax, ay], [bx, by]) => {
  const dx = bx - ax;
  const dy = by - ay;
  const t = dx || dy ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy))) : 0;
  return Math.hypot(px - ax - t * dx, py - ay - t * dy);
};

// Deux parcelles se touchent si un sommet de l'une est à moins de `toleranceM` d'un bord de l'autre.
export function areAdjacent(geometryA, geometryB, toleranceM = ADJACENCY_TOLERANCE_M) {
  const ringsA = outerRings(geometryA);
  const ringsB = outerRings(geometryB);
  if (!ringsA.length || !ringsB.length) return false;
  const a = bounds(ringsA);
  const b = bounds(ringsB);
  const project = projector((a.minLat + a.maxLat) / 2);
  const margin = (2 * toleranceM) / METERS_PER_DEGREE / Math.cos((a.minLat * Math.PI) / 180);
  if (a.minLon > b.maxLon + margin || b.minLon > a.maxLon + margin || a.minLat > b.maxLat + margin || b.minLat > a.maxLat + margin) {
    return false;
  }
  const near = (vertices, rings) => vertices.some((vertex) => rings.some((ring) => ring.some((point, i) =>
    i > 0 && pointSegmentDistance(project(vertex), project(ring[i - 1]), project(point)) <= toleranceM)));
  return near(ringsA.flat(), ringsB) || near(ringsB.flat(), ringsA);
}

// Groupes de parcelles contiguës (composantes connexes), en listes d'identifiants.
export function contiguousGroups(parcels) {
  const groups = [];
  const seen = new Set();
  parcels.forEach((start) => {
    if (seen.has(start.idu)) return;
    const group = [];
    const queue = [start];
    seen.add(start.idu);
    while (queue.length) {
      const current = queue.shift();
      group.push(current.idu);
      parcels.forEach((other) => {
        if (!seen.has(other.idu) && areAdjacent(current.geometry, other.geometry)) {
          seen.add(other.idu);
          queue.push(other);
        }
      });
    }
    groups.push(group);
  });
  return groups;
}

const insideRing = ([x, y], ring) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

export const containsPoint = (geometry, point) => outerRings(geometry).some((ring) => insideRing(point, ring));

// Part de la parcelle à l'intérieur du lasso (anneau [lon, lat]), par échantillonnage sur une grille.
export function shareInsideLasso(geometry, lassoRing, steps = 14) {
  const rings = outerRings(geometry);
  if (!rings.length || lassoRing.length < 3) return 0;
  const { minLon, maxLon, minLat, maxLat } = bounds(rings);
  let inParcel = 0;
  let inBoth = 0;
  for (let i = 0; i < steps; i++) {
    for (let j = 0; j < steps; j++) {
      const point = [minLon + ((i + 0.5) / steps) * (maxLon - minLon), minLat + ((j + 0.5) / steps) * (maxLat - minLat)];
      if (!rings.some((ring) => insideRing(point, ring))) continue;
      inParcel += 1;
      if (insideRing(point, lassoRing)) inBoth += 1;
    }
  }
  return inParcel ? inBoth / inParcel : 0;
}

// Totaux d'une sélection à partir des résultats /cbs_parcel (les parcelles encore en calcul ou
// en erreur sont ignorées ; `pending` dit combien manquent).
export function selectionTotals(results) {
  const done = results.filter((r) => r && r.surface_m2);
  const surface = done.reduce((sum, r) => sum + r.surface_m2, 0);
  const bbs = done.reduce((sum, r) => sum + (r.bbs_m2 ?? r.cbs * r.surface_m2), 0);
  const bbsPotential = done.reduce((sum, r) => sum + (r.bbs_potential_m2 ?? r.cbs * r.surface_m2), 0);
  return {
    surface,
    cbs: surface ? bbs / surface : null,
    cbsPotential: surface ? bbsPotential / surface : null,
    pending: results.length - done.length,
  };
}

const share = (result, names) => {
  const surfaces = result?.surfaces_m2 || {};
  return result?.surface_m2 ? names.reduce((sum, name) => sum + (surfaces[name] || 0), 0) / result.surface_m2 : 0;
};

export const builtShare = (result) => share(result, ['Bâtiment', 'Serre']);
export const imperviousShare = (result) => share(result, ['Zone imperméable']);

// Parcelle à regarder de près avant de la retenir.
export function parcelHint(result) {
  if (!result?.surface_m2) return null;
  if (builtShare(result) < 0.02 && imperviousShare(result) > 0.7) return 'Sans bâti, presque tout imperméable : voirie ou parking ?';
  if (builtShare(result) < 0.02 && result.surface_m2 > 5000) return 'Grande parcelle sans bâti : fait-elle partie du site ?';
  return null;
}

// Comparaison avec la surface de parcelle saisie dans le formulaire du bâtiment.
export function plotSurfaceCheck(totalM2, plotSurface) {
  const entered = parseFloat(plotSurface);
  if (!totalM2) return { state: 'none' };
  if (!entered) return { state: 'empty' };
  const gap = (totalM2 - entered) / entered;
  return { state: Math.abs(gap) > PLOT_SURFACE_TOLERANCE ? 'different' : 'ok', entered, gap };
}
